// Updated: 2026-09-03
//
// **THIS CONTEXT IS PERSONAL-ONLY.** It is ALWAYS the signed-in user's OWN craft room, and NEVER the
// craft room being viewed. That single fact is why `CraftRoom.tsx:50-53` renames every member it reads to
// `own*` — `craftRoom: ownCraftRoom`, `materials: ownMaterials`, and so on — and this line exists so the
// next reader does not have to reconstruct the rule from the renames. A visitor's craft room is a
// different resource on a different endpoint under a different key: `['craftRoom', screenName]`, read with
// `useCraftRoomPageQuery` directly by the page. Nothing about a visited craft room may ever be routed
// through here (react-query-craft-room, Req 5.9).
//
// Migrated to React Query in slice 8 (react-query-craft-room, Req 2, 4–10).
//
// ─── WHAT THIS PROVIDER IS NOW ────────────────────────────────────────────────────────────────────
//
// A THIN DERIVATION over ONE cache entry, `['me','craftRoom']`, and nothing else. It owns no server data:
// **ZERO `useState`**. Gone with the migration are all six server-data state slices (`craftRoom`,
// `materials`, `projects`, `publicProjects`, `privateProjects`, `archivedProjects`) plus `loading` and
// `error`, the whole hand-rolled `fetchCraftRoom` loader, and the logged-out clear effect. The cache holds
// the payload VERBATIM (`src/queries/craftRoom/queries.ts`, Req 1.9) and every member below is a pure
// function of it, computed at render time.
//
// The provider is KEPT rather than deleted in favour of converting its eight consumers because the blast
// radius spans three feature areas (Craft Room, Material pages, the shared copy button) and **six of the
// eight consumers change by zero lines** (Req 5.7, 5.8). Unlike slice 7, that is the whole argument: no
// test mocks `useCraftRoom` — there is no craft-room context test at all today — so the "frozen
// verification surface" half of slice 7's justification does not apply here, and the adapter's case is
// recorded as WEAKER on purpose rather than inherited.
//
// The name, the file, the export, the position in `src/index.tsx` and the THROWING `useCraftRoom()` hook
// are all unchanged, deliberately (Req 5.1, 5.2).
//
// ─── D4: WHY THERE IS NO PUBLIC FALLBACK ANY MORE (§ 5) ───────────────────────────────────────────
//
// The deleted `fetchCraftRoom` had an `else` arm that called the PUBLIC fetcher when `idToken` was absent
// and populated the same six state slices from a different payload. It is gone, and the query is simply
// DISABLED in that state (Req 4.1, 4.2, 13.4). Three reasons, in the order that decides it:
//
//   1. **The type argument.** The two payloads name their fields differently: the secure one carries
//      `materials` at the top level plus `public_projects` / `private_projects` / `archived_projects`; the
//      public one nests the inventory at `craftRoom.materials` and carries a single `projects`. `useState`
//      absorbed that divergence because six separate setters each translated their own branch — the STATE
//      was normalized even though the payloads never were. ONE cache entry has no setters to translate: it
//      holds whichever payload the last branch wrote, `getQueryData(craftRoomKeys.me())` is typed
//      `SecureCraftRoomPageResponse`, and after a fallback run **that type is a lie** (Req 4.3).
//   2. **What the branch actually DID when it ran** (Req 4.4): it forced `privateProjects` and
//      `archivedProjects` to `[]` with no error, no log and no user-visible signal. The owner saw a craft
//      room that looked complete and was not — a silent downgrade, which is the worst failure mode to keep
//      because nothing distinguishes it from the truth.
//   3. **Reachability** (Req 4.5): the branch required `user` AND `screen_name` AND no `idToken` — a
//      transient auth state, not a supported mode. And `enabled: false` does not evict, so it leaves the
//      last GOOD data in place (Req 2.5, 4.7) where the branch REPLACED good data with degraded data.
//
// If an owner-visible *public* read is ever genuinely wanted, the escape hatch is the Visitor_Entry with
// the owner's own screen name — `useCraftRoomPageQuery(screen_name)`, a public payload under a public key
// with the public type. It is written next to `page()` in `keys.ts` (Req 4.6). NEVER a public payload
// written into `me()`.
//
// ─── D3: THE SIGN-OUT LEAK NEEDS BOTH MECHANISMS, AND NEITHER IS SUFFICIENT ALONE ─────────────────
//
// The rule is slice 7's and is not re-derived here: a new `['me',…]` key is not finished until sign-out
// evicts it, and that needs BOTH a derive-guard AND a scoped `removeQueries` (Req 10.10, 10.11). What is
// specific to THIS key:
//
//   * **What leaks** is an INVENTORY — every material the previous user owns — plus their project titles,
//     including the PRIVATE and ARCHIVED ones. Not a follow list.
//   * **For how long.** Not ten minutes. `gcTime`'s timer starts only when the last observer unmounts, and
//     `CraftRoomProvider` sits above `App` and never unmounts while the tab lives, so without a purge the
//     entry is readable until the tab closes (Req 10.2).
//
// The two mechanisms are the `payload` derive-guard and the `removeQueries` transition effect, both below,
// each with its own note on the window the other leaves open.

import type {
  CraftRoomResponse,
  MaterialResponse,
  CraftProjectDisplayResponse,
  CraftRoomProjectListItem,
  SecureCraftRoomPageResponse,
} from '@mrb91002/shared-types'
import { useQueryClient } from '@tanstack/react-query'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { craftRoomKeys } from '../queries/craftRoom/keys'
import { useOwnCraftRoomQuery } from '../queries/craftRoom/queries'

import { useAuth } from './AuthContext'

// package-local — project type union for context (public API returns CraftRoomProjectListItem, secure API
// returns CraftProjectDisplayResponse). Stays website-local and is NOT moved into `@mrb91002/shared-types`
// (Req 5.10, 12.1): the union is a SYMPTOM of the two payloads answering two element types, not a shared
// contract, and this slice does not touch shared types.
type ContextProjectType = CraftProjectDisplayResponse | CraftRoomProjectListItem

/**
 * EXACTLY EIGHT MEMBERS (Req 6.2), narrowed here in this file (Req 6.9) from twelve.
 *
 * Four members were deleted from the interface AND the value literal because a grep across
 * `services/**​/src` and `integration-tests/` (excluding `services/website/dist/**` as build output) found
 * ZERO consumers of each (Req 6.1, 6.3):
 *
 *   | member            | every match outside this file                                    | verdict |
 *   |-------------------|-----------------------------------------------------------------|---------|
 *   | `publicProjects`  | none — declaration, two setters, the value literal               | dead    |
 *   | `privateProjects` | none — declaration, two setters, the value literal               | dead    |
 *   | `error`           | `CraftRoom.tsx:49` destructures `error` — from `useErrorHandler()`, NOT this context | dead |
 *   | `hasMaterial`     | none — declaration, the value literal                           | dead    |
 *
 * Two facts a reader will otherwise get wrong:
 *
 *   * **`error`'s removal removes NO behavior** (Req 6.4). No consumer rendered it and none branched on
 *     it; its only job was holding a message the `console.error` had ALREADY emitted. The logging survives
 *     (see the error-transition effect below); only the redundant copy of the message is gone.
 *   * **`hasMaterial` is NOT a rename of `getUserMaterialBySource`** (Req 6.5). Its predicate also matched
 *     `material_id`, which `getUserMaterialBySource` does not. Deleting it is deleting a DIFFERENT
 *     predicate, not renaming the surviving one.
 *
 * Removing the two project arrays does not touch `projects` (Req 6.6) — that member is derived from the
 * same three payload arrays, so nothing it rendered depended on the two removed views of them. And
 * **nothing removed is re-exposed as a hook in the query layer** (Req 6.7): there is no
 * `useHasMaterial` / `usePublicProjects` in `src/queries/craftRoom/`, because relocating dead surface is
 * not deleting it.
 */
interface CraftRoomContextType {
  craftRoom: CraftRoomResponse | null
  materials: MaterialResponse[]
  projects: ContextProjectType[]
  archivedProjects: ContextProjectType[]
  loading: boolean
  refetchCraftRoom: () => Promise<void>
  getUserMaterialBySource: (sourceMaterialId: string) => MaterialResponse | undefined
  updateMaterials: (updater: (materials: MaterialResponse[]) => MaterialResponse[]) => void
}

const CraftRoomContext = createContext<CraftRoomContextType | undefined>(undefined)

export const CraftRoomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  /**
   * ONE read. The gate — `!!user && !!screen_name && !!idToken` — lives in the hook, which reads all three
   * from `useAuth()` itself, so this provider holds no auth plumbing beyond the `user` it needs for the
   * purge below (`src/queries/craftRoom/queries.ts`).
   *
   * `isEnabled` is the observer's RESOLVED gate, taken straight off the query result rather than
   * recomputed here from `useAuth()`. That matters: a second copy of `!!user && !!screen_name && !!idToken`
   * in this file would be a SECOND DEFINITION OF THE GATE, two expressions that must agree forever and
   * are kept in agreement by nobody in particular — and the one that must not drift is the one the
   * derive-guard below is built on. Reading it from the query means the guard cannot disagree with the
   * fetch it is guarding.
   *
   * **This hook is also why the owner's craft room now loads on mount** (D1, BL-025's frontend half).
   * There is no imperative trigger anywhere and no new one was added: this provider wraps `App`, so the
   * Owner_Entry is populated for the whole authenticated shell. The pre-migration loader was reachable
   * only from a single route-gated call site in `CraftRoom.tsx`, which meant `getUserMaterialBySource`
   * answered from an EMPTY inventory for any session that never visited `/craft-room` — truthfully, as far
   * as the client knew, which is why a display defect drove a write.
   */
  const { data, isPending, error, isEnabled: enabled } = useOwnCraftRoomQuery()

  /**
   * THE DERIVE-GUARD — half of D3. **Every derived member below reads `payload`, NEVER `data`.**
   *
   * `enabled: false` does not EVICT — React Query keeps returning the last successful `data` for the key —
   * so `data` can still be the PREVIOUS session's craft room. Reading `data` directly anywhere below
   * re-opens the inventory leak this one line exists to close.
   *
   * It is not sufficient by itself, which is why the purge effect exists too: the entry survives in the
   * cache, and at the next sign-in `enabled` flips back to true on the very first authenticated render —
   * user, screen name and token all present — BEFORE that session's fetch resolves, so the now-passing
   * guard would hand back the previous user's inventory.
   */
  const payload = enabled ? data : undefined

  // Every coercion the old provider baked into its `.then` blocks now happens HERE, at derivation time,
  // and NONE of it at write time (Req 1.9, 5.4). The old provider stored the same projects three ways —
  // `publicProjects`, `privateProjects` and the concatenated `projects` — so a partial update to one left
  // consumers reading the others with a different answer. One cached payload cannot disagree with itself.
  const craftRoom = payload?.craftRoom ?? null
  const materials = useMemo(() => payload?.materials ?? [], [payload])
  /**
   * The `archived#` bucket, and ONLY that bucket — one key-condition query's result, not a filter.
   *
   * This is what makes the archived list correct rather than coincidentally right: it comes from the key
   * range, so it cannot disagree with the model the way a predicate over an item field can. The material
   * equivalent in `CraftRoom.tsx` was exactly such a predicate (`archived_at !== null`) and it was wrong
   * in the widest possible way — unarchive REMOVES the attribute, so `undefined !== null` classified every
   * ACTIVE material as archived (content-visibility-model task 29, BL-263).
   */
  const archivedProjects = useMemo<ContextProjectType[]>(() => payload?.archived_projects ?? [], [payload])

  /**
   * The owner's ACTIVE projects — `public → unlisted → private`, in exposure order.
   *
   * ## Two changes, both of them BL-263 (content-visibility-model Requirement 5.3, task 29)
   *
   * **`unlisted_projects` is now read.** It was not, and that was the live half of the defect: task 18
   * split the craft-room fetcher into one key-condition query per Listing_Bucket and added an
   * `unlisted_projects` array to the payload, and this concatenation named the other three — so an
   * unlisted project was queried, serialised, sent, and then silently dropped by the client. The owner
   * had no surface anywhere showing it. The array was added ahead of a consumer deliberately, because the
   * alternative was a bucket the API could not express; this is that consumer.
   *
   * **`archived_projects` is no longer concatenated in.** It was, which meant the "Published" tab in
   * `CraftProjectsSection` listed archived projects among active ones while the "Archived" tab listed
   * them again — one item in two places, and a combined listing spanning buckets, which is exactly what
   * Requirement 5.3 forbids. Archived projects reach that tab through {@link archivedProjects}, which is
   * the `archived#` bucket's own query, so nothing is lost by removing them from here.
   *
   * The comment this replaces recorded that the order was preserved "EXACTLY" from before the React Query
   * migration, on the grounds that changing it would silently reorder every owner's page. That reasoning
   * still holds for the relative order of the buckets — public first — and is why `unlisted` is inserted
   * in exposure order rather than appended. Removing `archived` is a deliberate departure from it, and the
   * departure is the point: the previous order was showing the wrong items, not merely showing them in a
   * particular sequence.
   *
   * Memoized (Req 5.5) so a re-render does not hand consumers a NEW array identity on every pass. The old
   * `projects` array was fresh on every fetch and `CraftRoom.tsx` carried it in a `useCallback` dependency
   * array, which is exactly how a visitor's public read ended up re-firing whenever the owner's context
   * state changed identity. Other consumers may take similar dependencies.
   */
  const projects = useMemo<ContextProjectType[]>(
    () => [
      ...(payload?.public_projects ?? []),
      ...(payload?.unlisted_projects ?? []),
      ...(payload?.private_projects ?? []),
    ],
    [payload],
  )

  /**
   * D5 — `loading` is now `true` on the FIRST ENABLED RENDER instead of `false` (Req 9.1, 9.2, 9.3, 13.5).
   *
   * `isPending`, **NOT `isFetching`**: a background refetch — every one of the nine post-write
   * `refetchCraftRoom()` sites, and every craft-room invalidation from `craftProject/mutations.ts` — must
   * leave `loading` FALSE, because the data is already on screen and flipping a spinner up over it would
   * turn an invisible refresh into a visible flicker (Req 9.4).
   *
   * The `enabled` conjunct is load-bearing in the other direction: a disabled query has no data and is
   * therefore permanently `isPending`, so without it a signed-out user would report `loading: true`
   * forever. With it, the disabled paths report `false` — byte-identical to the old `useState(false)` on
   * every path where the old `setLoading(true)` was never reached.
   *
   * On ERROR it is `false` (Req 9.5), so a failed owner read renders the signed-out-shaped defaults rather
   * than a spinner. The old `finally { setLoading(false) }` behaved the same way.
   */
  const loading = enabled ? isPending : false

  /**
   * THE OWNER READ'S LOGGING SURVIVES the deletion of the `error` member (Req 6.4).
   *
   * Keyed on the error INSTANCE, not on a boolean. The cache entry HOLDS the error, so a render-body
   * `console.error` would fire on every render for as long as the entry stays errored; a boolean latch
   * would go the other way and swallow a second, distinct failure in a later session. Instance identity
   * gives at-most-once per failure: idempotent for a held error, live for a new one. Message unchanged
   * from the deleted `catch`, so existing console-grep habits still work.
   */
  const loggedError = useRef<unknown>(null)
  useEffect(() => {
    if (!error || error === loggedError.current) return
    loggedError.current = error
    console.error('Failed to load craft room:', error)
  }, [error])

  /**
   * THE SIGN-OUT PURGE — the other half of D3, and the replacement for the deleted logged-out clear effect
   * and its six `setState` calls (Req 10.4, 10.6).
   *
   * Transition detection is a **ref holding the PREVIOUS authenticated state**, compared and then updated
   * inside the effect. It must be a transition and NOT a bare `if (!user)` (Req 10.5): the bare form also
   * fires on the very first mount of an anonymous session and on every unrelated re-render while signed
   * out, turning a one-shot eviction into a repeated one. Only authenticated→unauthenticated purges.
   *
   * Scoped to `['me','craftRoom']` **EXACTLY** (Req 10.7) — not `removeQueries({ queryKey: ['me'] })`. The
   * general `['me',…]`-wide purge belongs to slice 9 (`AuthContext`), and two purge sites kept in agreement
   * by nobody in particular is worse than one. **No edit to `AuthContext.tsx`** (Req 10.9, 12.7).
   *
   * The Visitor_Entry `['craftRoom', screenName]` is deliberately UNTOUCHED (Req 10.8): it is public and
   * survives a sign-out like every other public entry.
   *
   * Not sufficient by itself, which is why the derive-guard above exists too: effects fire AFTER commit, so
   * between the last authenticated render and this purge there is at least one committed render where
   * `user` is already null while the cache still holds the previous session's inventory.
   */
  const wasAuthenticated = useRef(!!user)
  useEffect(() => {
    const previouslyAuthenticated = wasAuthenticated.current
    wasAuthenticated.current = !!user

    if (previouslyAuthenticated && !user) {
      queryClient.removeQueries({ queryKey: craftRoomKeys.me() })
    }
  }, [user, queryClient])

  /**
   * Still a bare `() => Promise<void>` and it still **RETURNS** the promise, so all nine post-write refresh
   * call sites keep `await`ing it unedited — including the two that are prop passes (`CraftRoom.tsx:375`,
   * `:377`) and could not have been given an argument (Req 8.1, 8.5).
   *
   * The body is ONE invalidation, scoped to ONE key (Req 8.7): a `queryKey: ['me']` typo here would refetch
   * every personal query in the app after every material edit. With the query DISABLED it resolves and
   * issues zero fetches (Req 8.6) — `invalidateQueries` marks the entry stale, and a disabled observer does
   * not act on staleness.
   *
   * It no longer does two jobs. The pre-migration function was both the app's only LOAD and its nine
   * refreshes, and the load/refresh conflation is what the `!ownCraftRoom` gate in `CraftRoom.tsx` existed
   * to arbitrate. `useQuery` owns loading now; this owns refreshing.
   */
  const refetchCraftRoom = useCallback(
    (): Promise<void> => queryClient.invalidateQueries({ queryKey: craftRoomKeys.me() }),
    [queryClient],
  )

  /**
   * Signature byte-identical to the pre-migration member (Req 5.6), including the
   * `MaterialResponse | undefined` return and the EXACT predicate. Both disjuncts and their order are
   * preserved because `MaterialCopyButton.tsx:26` relies on the `?? null` at the call site and
   * `PublicMaterialPage.tsx:442`'s branch turns on `undefined` versus a hit.
   *
   * This is the helper whose answer D1 changes: it now answers from a POPULATED inventory for every
   * authenticated session, not just the ones that visited `/craft-room`.
   */
  const getUserMaterialBySource = useCallback(
    (sourceMaterialId: string): MaterialResponse | undefined =>
      materials.find(
        (m) => m.source_material_id === sourceMaterialId || m.public_material_id === sourceMaterialId,
      ),
    [materials],
  )

  /**
   * Signature byte-identical (Req 7.1), now a `setQueryData` on the Owner_Entry that replaces
   * `payload.materials` and leaves EVERY OTHER FIELD UNTOUCHED (Req 7.2).
   *
   * **THE FUNCTIONAL UPDATER FORM IS REQUIRED, NOT OPTIONAL** (Req 7.3). All three Material_Grid_Writers
   * pass a function — `CraftRoom.tsx:485` after `assignMaterialsToFolders`, `CraftRoom.tsx:601` from the
   * contextual action bar, plus `useCellEditing` and `useDragDrop` — and the `setMaterials(updater)` this
   * replaces gave them React's queued-updater semantics, where two writes in the same tick **COMPOSE**. A
   * value-based `setQueryData(key, { ...data, materials: updater(materials) })` closing over a
   * render-time `materials` would let the second write CLOBBER the first: invisible in every single-write
   * test, and visible in production only as lost edits under fast grid interaction. The `prev` the updater
   * receives is the CURRENT CACHED payload at write time, which is what makes composition work.
   *
   * **When the entry holds no data this is a NO-OP** (Req 7.4) — returning `prev` (`undefined`) creates
   * nothing. Synthesizing `{ materials: [...] }` would put a shape the fetcher never returns into an entry
   * typed `SecureCraftRoomPageResponse`: the D4 defect by a different route.
   *
   * **This write INVALIDATES NOTHING — do not add an invalidation here** (Req 7.5). Same words, and the
   * same reason, as `craftProject/mutations.ts` carries at `useSaveProjectDraftMutation` and
   * `useSaveTempProjectMutation`: these are editor/autosave writes — an inline cell edit, a drag-drop
   * folder reorder, a pending-edit buffer — and a background refetch would race the author's keystrokes and
   * revert cells they are still typing into. The next reader's instinct will be that a write without an
   * invalidation is an oversight. It is not.
   *
   * Identity is stable across renders (Req 7.7) — `queryClient` is the only dependency — so the three
   * consumers can keep it in dependency arrays without re-running their effects every pass.
   *
   * Rollback stays where it is (Req 7.8): `rollbackFolderAssignment` in `useDragDrop` and the pending-edit
   * revert paths in `useCellEditing` and `CraftRoomInventoryContent` already restore pre-edit materials
   * THROUGH THIS SAME UPDATER, so no query-level snapshot is added.
   *
   * THE ORDERING HAZARD (§ 7.9), and why it is already handled: a `setQueryData` and an in-flight refetch
   * of the same key can resolve in either order. Where a grid write is followed by a server round-trip the
   * call site already awaits the round-trip and THEN refreshes — `await batchUpdateMaterials(...)` then
   * `await refetchCraftRoom()` (`CraftRoom.tsx:625-631`) — so the authoritative payload arrives after the
   * write it would overwrite, which is the intended order. The remaining window (a purely local edit
   * landing mid-refetch) is the window `setMaterials` had too: the old fetch's
   * `setMaterials(secureData.materials || [])` overwrote local edits exactly the same way. The migration
   * does not widen it.
   */
  const updateMaterials = useCallback(
    (updater: (materials: MaterialResponse[]) => MaterialResponse[]): void => {
      queryClient.setQueryData<SecureCraftRoomPageResponse>(craftRoomKeys.me(), (prev) =>
        prev ? { ...prev, materials: updater(prev.materials) } : prev,
      )
    },
    [queryClient],
  )

  // EXACTLY EIGHT MEMBERS — see `CraftRoomContextType` above for the per-member grep. Property 12 asserts
  // `Object.keys(value).sort()` structurally, so a removed member cannot reappear unnoticed and a new one
  // cannot arrive unannounced.
  const value = useMemo<CraftRoomContextType>(
    () => ({
      craftRoom,
      materials,
      projects,
      archivedProjects,
      loading,
      refetchCraftRoom,
      getUserMaterialBySource,
      updateMaterials,
    }),
    [
      craftRoom,
      materials,
      projects,
      archivedProjects,
      loading,
      refetchCraftRoom,
      getUserMaterialBySource,
      updateMaterials,
    ],
  )

  return <CraftRoomContext.Provider value={value}>{children}</CraftRoomContext.Provider>
}

// ---- Hook ----
// THROWS outside its provider, deliberately and permanently, with its message unchanged (Req 5.2). A
// component rendered outside the authenticated shell must keep failing loudly rather than silently reading
// defaults — softening this to return defaults would turn every such mistake into a silent empty inventory,
// which is the exact shape of the defect this slice just closed.
export const useCraftRoom = () => {
  const context = useContext(CraftRoomContext)
  if (context === undefined) {
    throw new Error('useCraftRoom must be used within a CraftRoomProvider')
  }
  return context
}
